import React, { useState } from 'react';
import styled from '@emotion/styled';
import { VisitorRegistrationRequest } from '../types';
import AddressModal from '../components/AddressModal';
import { validateForm, formatPhoneNumber } from '../utils/validation';
import { createVisitor } from '../api/visitor';

const VisitorRegistration = () => {
  // 폼 데이터 상태 관리
  const [formData, setFormData] = useState<VisitorRegistrationRequest>({
    visitor_name: '',
    visitor_phone: '',
    visitor_email: '',
    visitor_company_name: '',
    visitor_company_address: '',
    visitor_company_detail_address: '',
    visitor_position: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState('');

  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  // 주소 선택 완료 핸들러 추가
  const handleAddressComplete = (address: string) => {
    setFormData((prev) => ({
      ...prev,
      visitor_company_address: address,
    }));

    // 주소 선택 시 상세주소 입력란으로 포커스 이동 (선택사항)
    setTimeout(() => {
      const detailInput = document.querySelector('input[name="visitor_company_detail_address"]') as HTMLInputElement;
      detailInput?.focus();
    }, 100);
  };

  // 입력 핸들러
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;

    const formattedValue = name === 'visitor_phone' ? formatPhoneNumber(value) : value;

    setFormData((prev) => ({
      ...prev,
      [name]: formattedValue,
    }));
    // 입력 시 해당 필드 에러 메세지 제거
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  // 폼 제출 핸들러
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // 유효성 검증
    const validationErrors = validateForm(formData);

    // 에러가 있으면 표시하고 중단
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);

      // 첫 번째 에러 필드로 스크롤
      const firstErrorField = Object.keys(validationErrors)[0];
      const element = document.querySelector(`[name="${firstErrorField}"]`);
      element?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    // API 호출 시작
    setIsLoading(true);
    setErrors({});
    setSuccessMessage('');

    try {
      const response = await createVisitor(formData);
      // 성공 처리
      setSuccessMessage(
        `등록이 완료되었습니다!\n` +
          `참가 고유번호: ${response.data.visitor_confirm_code}\n` +
          `비즈톡으로 안내 메시지가 발송됩니다.`,
      );
      // 폼 초기화
      setFormData({
        visitor_name: '',
        visitor_phone: '',
        visitor_email: '',
        visitor_company_name: '',
        visitor_company_address: '',
        visitor_company_detail_address: '',
        visitor_position: '',
      });

      // 성공 메세지로 스크롤
      setTimeout(() => {
        const successElement = document.querySelector('.success-message');
        successElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 100);
    } catch (error: any) {
      console.error('Registration error:', error);

      if (error.error_type === 'duplicate_user') {
        // 중복 에러 처리
        if (error.message.includes('휴대폰')) {
          setErrors({ visitor_phone: error.message });
        } else if (error.message.includes('이메일')) {
          setErrors({ visitor_email: error.message });
        }
      } else if (error.errors) {
        const newErrors: { [key: string]: string } = {};
        Object.keys(error.errors).forEach((key) => {
          newErrors[key] = error.errors[key][0];
        });
        setErrors(newErrors);
      } else {
        setErrors({
          general: error.message || '등록 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Container>
      <FormWrapper>
        <Title>삼성화재 Startup inside 2025</Title>
        <Subtitle>방문객 등록</Subtitle>
        <Form onSubmit={handleSubmit}>
          {/** 일반 에러 메세지 */}
          {errors.general && (
            <ErrorBox>
              <ErrorIcon>⚠️</ErrorIcon>
              {errors.general}
            </ErrorBox>
          )}
          <InputGroup>
            <Label>이름 *</Label>
            <Input
              type="text"
              name="visitor_name"
              value={formData.visitor_name}
              onChange={handleInputChange}
              placeholder="성함을 입력해주세요"
              disabled={isLoading}
            />
            {errors.visitor_name && <ErrorText>{errors.visitor_name}</ErrorText>}
          </InputGroup>
          <InputGroup>
            <Label>전화번호 *</Label>
            <Input
              type="tel"
              name="visitor_phone"
              value={formData.visitor_phone}
              onChange={handleInputChange}
              placeholder="010-1234-5678"
              disabled={isLoading}
            />
            {errors.visitor_phone && <ErrorText>{errors.visitor_phone}</ErrorText>}
          </InputGroup>
          <InputGroup>
            <Label>이메일 *</Label>
            <Input
              type="email"
              name="visitor_email"
              value={formData.visitor_email}
              onChange={handleInputChange}
              placeholder="example@samsung.com"
              disabled={isLoading}
            />
            {errors.visitor_email && <ErrorText>{errors.visitor_email}</ErrorText>}
          </InputGroup>
          <InputGroup>
            <Label>회사명 *</Label>
            <Input
              type="text"
              name="visitor_company_name"
              value={formData.visitor_company_name}
              onChange={handleInputChange}
              placeholder="회사명을 입력해주세요"
              disabled={isLoading}
            />
            {errors.visitor_name && <ErrorText>{errors.visitor_name}</ErrorText>}
          </InputGroup>
          <InputGroup>
            <Label>회사주소 *</Label>
            <AddressInputWrapper>
              <Input
                type="text"
                name="visitor_company_address"
                value={formData.visitor_company_address}
                onClick={() => setIsAddressModalOpen(true)}
                placeholder="클릭하여 주소를 검색하세요"
                readOnly
                disabled={isLoading}
                style={{ cursor: 'pointer', flex: 1 }}
              />
              <SearchButton type="button" onClick={() => setIsAddressModalOpen(true)} disabled={isLoading}>
                주소 검색
              </SearchButton>
            </AddressInputWrapper>
            {errors.visitor_company_address && <ErrorText>{errors.visitor_company_address}</ErrorText>}
          </InputGroup>
          <InputGroup>
            <Label>상세주소 *</Label>
            <Input
              type="text"
              name="visitor_company_detail_address"
              value={formData.visitor_company_detail_address}
              onChange={handleInputChange}
              placeholder="예) 테헤란로 123 OO빌딩 5층"
              disabled={isLoading || !formData.visitor_company_address}
            />
            {errors.visitor_company_detail_address && <ErrorText>{errors.visitor_company_detail_address}</ErrorText>}
          </InputGroup>
          <InputGroup>
            <Label>직급 *</Label>
            <Input
              type="text"
              name="visitor_position"
              value={formData.visitor_position}
              onChange={handleInputChange}
              placeholder="예) 대리, 과장, 차장, 부장"
              disabled={isLoading}
            />
            {errors.visitor_position && <ErrorText>{errors.visitor_position}</ErrorText>}
          </InputGroup>
          {/** 임시 제출 버튼 */}
          <SubmitButton type="submit" disabled={isLoading}>
            {isLoading ? '등록중...' : '등록하기'}
          </SubmitButton>
        </Form>
        {/** 성공 메세지 */}
        {successMessage && (
          <SuccessMessage className="success-message">
            {successMessage.split('\n').map((line, index) => (
              <div key={index}>{line}</div>
            ))}
          </SuccessMessage>
        )}

        {/** 주소 검색 모달 추가 */}
        <AddressModal
          isOpen={isAddressModalOpen}
          onClose={() => setIsAddressModalOpen(false)}
          onComplete={handleAddressComplete}
        />
      </FormWrapper>
    </Container>
  );
};

export default VisitorRegistration;

// 스타일 컴포넌트
const Container = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #764ba2 0%, #667eea 33%, #4a5fd8 66%, #003cdc 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 20px;
`;

const FormWrapper = styled.div`
  background: white;
  border-radius: 12px;
  padding: 40px;
  width: 100%;
  max-width: 500px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  font-size: 24px;
  font-weight: bold;
  color: #333;
  text-align: center;
  margin-bottom: 8px;
`;

const Subtitle = styled.h2`
  font-size: 18px;
  font-weight: normal;
  color: #666;
  text-align: center;
  margin-bottom: 32px;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const Input = styled.input`
  padding: 12px;
  border: 1px solid #ddd;
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #667eea;
  }

  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
  }
`;

const ErrorText = styled.span`
  font-size: 12px;
  color: #e74c3c;
`;

const SubmitButton = styled.button`
  padding: 14px;
  background: linear-gradient(135deg, #003cdc 0%, #5563ea 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  transition: transform 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SuccessMessage = styled.div`
  margin-top: 20px;
  padding: 12px;
  background-color: #d4edda;
  color: #155724;
  border-radius: 8px;
  text-align: center;
`;

const AddressInputWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

const SearchButton = styled.button`
  padding: 12px 20px;
  background: linear-gradient(135deg, #003cdc 0%, #5563ea 100%);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.3s;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 8px rgba(102, 126, 234, 0.4);
  }

  &:disabled {
    background: #ccc;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  padding: 12px 16px;
  background-color: #fee;
  border: 1px solid #fcc;
  border-radius: 8px;
  color: #c00;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ErrorIcon = styled.span`
  font-size: 20px;
`;
